import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Board, BoardsApi } from './boardsApi';
import { navigate } from './router';
import { STICKY_COLORS } from './sticky';
import { TEMPLATES, type TemplateId } from './templates';
import { excalidrawClientColor, initials, Modal, relativeTime, toast } from './ui';
import type { DriveFolder } from '../../shared/src/boardDrive';
import { ChooseFoldersDialog, FolderField, NeedsFolderBanner } from '../../shared/src/components/BoardDrive';
import { driveApi, openShare } from './drive';

interface Props {
  boards: BoardsApi;
  user: { id: string; name: string; email: string };
  onSignOut(): void;
}

export function Dashboard({ boards, user, onSignOut }: Props) {
  const [list, setList] = useState<Board[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<Board | null>(null);
  const [deleting, setDeleting] = useState<Board | null>(null);
  const [choosing, setChoosing] = useState(false);

  const load = useCallback((again = true) => {
    boards
      .listPage()
      .then(({ boards: b, refreshing }) => {
        setList(b);
        setError(null);
        // The server is checking Drive for boards shared with you: look once more.
        if (refreshing && again) setTimeout(() => load(false), 2500);
      })
      .catch((e: Error) => setError(e.message));
  }, [boards]);
  useEffect(() => load(), [load]);
  const needsFolder = useMemo(() => (list ?? []).filter((b) => b.drive?.needs_folder), [list]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!list) return [];
    return s ? list.filter((b) => b.title.toLowerCase().includes(s) || b.owner_name.toLowerCase().includes(s)) : list;
  }, [list, q]);

  return (
    <div className="xb-dash">
      <header className="xb-dash-head">
        <div className="xb-brand">
          <span className="xb-brand-mark" aria-hidden />
          Excalidraw Board
        </div>
        <input className="xb-search" type="search" placeholder="Search boards" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search boards" />
        <div className="xb-grow" />
        <button className="xb-btn xb-btn-primary" onClick={() => setCreating(true)}>
          + New board
        </button>
        <div className="xb-me" title={user.email}>
          <span className="xb-avatar" style={{ background: excalidrawClientColor(String(user.id)) }}>
            {initials(user.name || user.email)}
          </span>
          <button className="xb-link" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <main className="xb-dash-main">
        {error && <p className="xb-error">Could not load boards: {error}</p>}
        {!list && !error && <p className="xb-muted">Loading boards…</p>}
        {list && list.length === 0 && (
          <div className="xb-empty">
            <p>No boards yet.</p>
            <button className="xb-btn xb-btn-primary" onClick={() => setCreating(true)}>
              Create your first board
            </button>
          </div>
        )}
        <NeedsFolderBanner count={needsFolder.length} onReview={() => setChoosing(true)} />
        {list && list.length > 0 && filtered.length === 0 && <p className="xb-muted">No boards match “{q}”.</p>}
        <div className="xb-grid">
          {filtered.map((b) => (
            <BoardCard key={b.id} board={b} onRename={() => setRenaming(b)} onDelete={() => setDeleting(b)} />
          ))}
        </div>
      </main>

      {creating && (
        <CreateDialog
          onClose={() => setCreating(false)}
          onCreate={async (title, template, folder) => {
            const b = await boards.create(title, template, folder?.id);
            navigate(`/board/${b.id}`);
          }}
        />
      )}
      {choosing && (
        <ChooseFoldersDialog
          api={driveApi}
          boards={needsFolder}
          onDone={(id, drive) => setList((l) => l?.map((x) => (x.id === id ? { ...x, drive } : x)) ?? l)}
          onClose={() => setChoosing(false)}
        />
      )}
      {renaming && (
        <RenameDialog
          board={renaming}
          onClose={() => setRenaming(null)}
          onSave={async (title) => {
            const b = await boards.rename(renaming.id, title);
            setList((l) => l?.map((x) => (x.id === b.id ? { ...x, ...b } : x)) ?? l);
            setRenaming(null);
          }}
        />
      )}
      {deleting && (
        <Modal title="Delete board" onClose={() => setDeleting(null)}>
          <p>
            Delete <strong>{deleting.title}</strong> for everyone? This cannot be undone from the app.
          </p>
          <div className="xb-modal-actions">
            <button className="xb-btn" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button
              className="xb-btn xb-btn-danger"
              onClick={async () => {
                try {
                  await boards.remove(deleting.id);
                  setList((l) => l?.filter((x) => x.id !== deleting.id) ?? l);
                  toast('Board deleted');
                } catch (e) {
                  toast(`Delete failed: ${(e as Error).message}`, 'error');
                }
                setDeleting(null);
              }}
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function BoardCard({ board, onRename, onDelete }: { board: Board; onRename(): void; onDelete(): void }) {
  const [menu, setMenu] = useState(false);
  const open = () => navigate(`/board/${board.id}`);
  const thumb = board.thumbnail_url ? `${board.thumbnail_url}${board.thumbnail_url.includes('?') ? '&' : '?'}v=${encodeURIComponent(board.updated_at)}` : null;
  return (
    <div className="xb-card" data-board-id={board.id}>
      <button className="xb-card-thumb" onClick={open} aria-label={`Open ${board.title}`}>
        {thumb ? <img src={thumb} alt="" loading="lazy" /> : <PlaceholderThumb template={board.template} />}
      </button>
      <div className="xb-card-body">
        <button className="xb-card-title" onClick={open} title={board.title}>
          {board.title || 'Untitled board'}
        </button>
        <div className="xb-card-meta">
          {board.role === 'owner' ? 'You' : board.owner_name} · {relativeTime(board.updated_at)}
          {board.member_count > 1 && ` · ${board.member_count} members`}
          {board.drive?.folder_name ? <div className="xb-small xb-muted" title={board.drive.folder_name}>📁 {board.drive.folder_name.split(' / ').pop()}</div> : null}
          {board.drive?.needs_folder ? <div className="xb-small xb-muted" data-testid="card-needs-folder">Only you · not in Drive yet</div> : null}
        </div>
      </div>
      <div className="xb-card-menu">
        <button className="xb-icon-btn" aria-label="Board actions" onClick={() => setMenu((m) => !m)}>
          ⋯
        </button>
        {menu && (
          <div className="xb-menu" onMouseLeave={() => setMenu(false)}>
            <button onClick={open}>Open</button>
            <button
              onClick={() => {
                setMenu(false);
                openShare(board.id);
              }}
            >
              {board.drive?.needs_folder ? 'Choose Drive folder…' : 'Share…'}
            </button>
            {board.role !== 'viewer' && (
              <button
                onClick={() => {
                  setMenu(false);
                  onRename();
                }}
              >
                Rename
              </button>
            )}
            {board.role === 'owner' && (
              <button
                className="xb-danger"
                onClick={() => {
                  setMenu(false);
                  onDelete();
                }}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PlaceholderThumb({ template }: { template: string }) {
  const c = Object.values(STICKY_COLORS);
  return (
    <svg viewBox="0 0 160 100" className="xb-ph" aria-hidden>
      {template === 'flowchart' ? (
        <g fill="none" stroke="currentColor" strokeWidth="2" opacity=".5">
          <ellipse cx="80" cy="16" rx="18" ry="8" />
          <rect x="62" y="34" width="36" height="16" rx="3" />
          <path d="M80 60 l14 10 -14 10 -14 -10z" />
          <path d="M80 24v10M80 50v10" />
        </g>
      ) : (
        [0, 1, 2, 3, 4, 5].map((i) => <rect key={i} x={18 + (i % 3) * 44} y={16 + Math.floor(i / 3) * 40} width="32" height="30" rx="2" fill={c[(i + template.length) % c.length]} />)
      )}
    </svg>
  );
}

function CreateDialog({ onClose, onCreate }: { onClose(): void; onCreate(title: string, t: TemplateId, folder: DriveFolder | null): Promise<void> }) {
  const [template, setTemplate] = useState<TemplateId>('blank');
  const [title, setTitle] = useState('');
  const [folder, setFolder] = useState<(DriveFolder & { pathLabel: string }) | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await onCreate(title.trim() || (template === 'blank' ? 'Untitled board' : TEMPLATES.find((t) => t.id === template)!.name), template, folder);
    } catch (e) {
      toast(`Could not create board: ${(e as Error).message}`, 'error');
      setBusy(false);
    }
  };
  return (
    <Modal title="New board" onClose={onClose} wide>
      <div className="xb-templates" role="radiogroup" aria-label="Template">
        {TEMPLATES.map((t) => (
          <button key={t.id} role="radio" aria-checked={template === t.id} className={`xb-template${template === t.id ? ' is-active' : ''}`} onClick={() => setTemplate(t.id)} data-template={t.id}>
            <PlaceholderThumb template={t.id === 'blank' ? '' : t.id} />
            <strong>{t.name}</strong>
            <span className="xb-small xb-muted">{t.description}</span>
          </button>
        ))}
      </div>
      <label className="xb-field">
        <span>Title</span>
        <input autoFocus value={title} placeholder="Untitled board" onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
      </label>
      <FolderField api={driveApi} value={folder} onChange={setFolder} />
      <div className="xb-modal-actions">
        <button className="xb-btn" onClick={onClose}>
          Cancel
        </button>
        <button className="xb-btn xb-btn-primary" disabled={busy} onClick={submit}>
          {busy ? 'Creating…' : 'Create board'}
        </button>
      </div>
    </Modal>
  );
}

function RenameDialog({ board, onClose, onSave }: { board: Board; onClose(): void; onSave(t: string): Promise<void> }) {
  const [title, setTitle] = useState(board.title);
  const save = () => {
    const t = title.trim();
    if (!t) return;
    onSave(t).catch((e: Error) => toast(`Rename failed: ${e.message}`, 'error'));
  };
  return (
    <Modal title="Rename board" onClose={onClose}>
      <label className="xb-field">
        <span>Title</span>
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} />
      </label>
      <div className="xb-modal-actions">
        <button className="xb-btn" onClick={onClose}>
          Cancel
        </button>
        <button className="xb-btn xb-btn-primary" onClick={save}>
          Save
        </button>
      </div>
    </Modal>
  );
}
